/**
 * Night Shift Declaration Create Form
 *
 * Displays a calendar grid of the current month with checkboxes for each day.
 * Features:
 * - Already-declared days are disabled; past days within the current month remain selectable
 * - Each week row has a "select all" toggle
 * - A "select full month" toggle at the top
 * - Admin users can use an EmployeePicker autocomplete to declare on behalf of another employee
 *
 * @module night-shift-declarations/ApplyNightShiftDeclaration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Alert,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Divider,
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import { EmployeePicker } from '../timeoff/EmployeePicker';
import {
    createNightShiftDeclaration,
    getNightShiftDeclarations,
    Employee,
} from '../dataProvider';
import { buildCalendarGrid, DayEntry, WeekEntry, DAY_LABELS } from './calendarUtils';

export const ApplyNightShiftDeclaration: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
    const [existingDates, setExistingDates] = useState<Set<string>>(new Set());
    const [onBehalfOf, setOnBehalfOf] = useState<Employee | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const monthName = now.toLocaleString('default', { month: 'long', year: 'numeric' });

    // Check admin permission
    useEffect(() => {
        const checkAdmin = Array.isArray(permissions) && permissions.includes('night-shift-declarations.admin');
        setIsAdmin(checkAdmin);
    }, [permissions]);

    // Fetch existing declarations for the current month
    useEffect(() => {
        const fetchExisting = async () => {
            setLoading(true);
            try {
                const data = await getNightShiftDeclarations();
                const monthStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
                const dates = new Set(
                    data
                        .filter(d => d.shift_date.startsWith(monthStr) && d.status !== 'Cancelled')
                        .map(d => d.shift_date)
                );
                setExistingDates(dates);
            } catch (err) {
                console.error('Error fetching existing declarations:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchExisting();
    }, [currentYear, currentMonth]);

    const weeks = buildCalendarGrid(currentYear, currentMonth, existingDates);

    /**
     * Returns all selectable (non-declared) days from a given week.
     * Past days within the current month are included as selectable.
     *
     * @param week - The week entry.
     * @returns Array of selectable day entries.
     */
    const getSelectableDaysInWeek = useCallback((week: WeekEntry): DayEntry[] => {
        return week.days.filter(
            (d): d is DayEntry => d !== null && !d.isAlreadyDeclared
        );
    }, []);

    /**
     * Returns all selectable days across the entire month.
     *
     * @returns Array of selectable day entries.
     */
    const getAllSelectableDays = useCallback((): DayEntry[] => {
        return weeks.flatMap(w => getSelectableDaysInWeek(w));
    }, [weeks, getSelectableDaysInWeek]);

    /**
     * Toggles selection for a single date.
     *
     * @param date - ISO date string.
     */
    const toggleDate = (date: string) => {
        setSelectedDates(prev => {
            const next = new Set(prev);
            if (next.has(date)) {
                next.delete(date);
            } else {
                next.add(date);
            }
            return next;
        });
    };

    /**
     * Toggles all selectable days in a given week.
     *
     * @param week - The week entry to toggle.
     */
    const toggleWeek = (week: WeekEntry) => {
        const selectable = getSelectableDaysInWeek(week);
        const allSelected = selectable.every(d => selectedDates.has(d.date));

        setSelectedDates(prev => {
            const next = new Set(prev);
            selectable.forEach(d => {
                if (allSelected) {
                    next.delete(d.date);
                } else {
                    next.add(d.date);
                }
            });
            return next;
        });
    };

    /**
     * Toggles all selectable days in the entire month.
     */
    const toggleFullMonth = () => {
        const all = getAllSelectableDays();
        const allSelected = all.every(d => selectedDates.has(d.date));

        setSelectedDates(prev => {
            const next = new Set(prev);
            all.forEach(d => {
                if (allSelected) {
                    next.delete(d.date);
                } else {
                    next.add(d.date);
                }
            });
            return next;
        });
    };

    /**
     * Submits the selected dates as night shift declarations.
     *
     * When no employee has been selected via the picker, `employee_id` is omitted
     * from the payload so that the backend defaults to the authenticated user.
     */
    const handleSubmit = async () => {
        if (selectedDates.size === 0) {
            setError('Please select at least one date');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const payload: { employee_id?: string; shift_dates: string[] } = {
                shift_dates: Array.from(selectedDates).sort(),
            };
            if (onBehalfOf) {
                payload.employee_id = onBehalfOf.employee_id;
            }
            await createNightShiftDeclaration(payload);

            notify('Night shift declaration submitted successfully', { type: 'success' });
            navigate('/night-shift-declarations');
        } catch (err: unknown) {
            const errorMessage = (err as Record<string, Record<string, string>>)?.body?.message
                || (err as Error)?.message
                || 'Failed to submit declaration';
            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const allSelectableDays = getAllSelectableDays();
    const isFullMonthSelected = allSelectableDays.length > 0 && allSelectableDays.every(d => selectedDates.has(d.date));

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" gutterBottom>
                    Declare Night Shift - {monthName}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isAdmin && (
                    <Box mb={2} data-testid="employee-picker-container">
                        <EmployeePicker
                            label="Apply on Behalf of (Optional)"
                            value={onBehalfOf}
                            onChange={setOnBehalfOf}
                            disabled={submitting}
                        />
                    </Box>
                )}

                {/* Full month toggle */}
                <Box mb={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isFullMonthSelected}
                                onChange={toggleFullMonth}
                                data-testid="select-full-month"
                            />
                        }
                        label="Select Full Month"
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Day-of-week headers */}
                <Box display="flex" mb={1}>
                    <Box sx={{ width: 100 }} />
                    {DAY_LABELS.map(label => (
                        <Box key={label} sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="caption" fontWeight="bold">{label}</Typography>
                        </Box>
                    ))}
                </Box>

                {/* Calendar weeks */}
                {weeks.map((week) => {
                    const selectableDays = getSelectableDaysInWeek(week);
                    const allWeekSelected = selectableDays.length > 0 && selectableDays.every(d => selectedDates.has(d.date));

                    return (
                        <Box key={week.weekIndex} display="flex" alignItems="center" mb={0.5}>
                            {/* Week toggle */}
                            <Box sx={{ width: 100 }}>
                                {selectableDays.length > 0 && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={allWeekSelected}
                                                onChange={() => toggleWeek(week)}
                                                size="small"
                                                data-testid={`select-week-${week.weekIndex}`}
                                            />
                                        }
                                        label={<Typography variant="caption">Week</Typography>}
                                    />
                                )}
                            </Box>

                            {/* Day checkboxes */}
                            {week.days.map((dayEntry, idx) => (
                                <Box key={idx} sx={{ flex: 1, textAlign: 'center' }}>
                                    {dayEntry ? (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={selectedDates.has(dayEntry.date)}
                                                    onChange={() => toggleDate(dayEntry.date)}
                                                    disabled={dayEntry.isAlreadyDeclared}
                                                    size="small"
                                                    data-testid={`day-${dayEntry.date}`}
                                                />
                                            }
                                            label={
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        dayEntry.isPast ? 'text.disabled'
                                                            : dayEntry.isAlreadyDeclared ? 'info.main'
                                                                : 'text.primary'
                                                    }
                                                >
                                                    {dayEntry.day}
                                                </Typography>
                                            }
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.disabled">&nbsp;</Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    );
                })}

                <Divider sx={{ mt: 2, mb: 2 }} />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        {selectedDates.size} day(s) selected
                    </Typography>
                    <Box display="flex" gap={1}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/night-shift-declarations')}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting || selectedDates.size === 0}
                        >
                            {submitting ? <CircularProgress size={20} /> : 'Submit Declaration'}
                        </Button>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};
